import { CloseIcon, SocCompleteIcon } from "../../../components/Icons";
import { MODALS, customIconsConfig } from "../../../config/params";
import React, { useEffect, useState } from "react";

import AssignPaymentPetCardList from "./AssignPaymentPetCardList";
import CardProviderDetails from "../../../components/CardProviderDetails/CardProviderDetails";
import { CentralLoadingIndicator } from "../../../components";
import { CircularProgress } from "@mui/material";
import { IWellnessPlanByUser } from "../../../services/wellness-plans-by-user/wellness-plan-by-user.interface";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { PAGES_PATH } from "../../../navigation/params";
import { StyledBookingFlowButton } from "../../containers/BookingScheduleContainer/BookingScheduleContainer.style";
import { toastMessageActions } from "../../../state/request";
import { useCardDetailStore } from "../../../services/card-detail/card-detail.service";
import { useDispatch } from "react-redux";
import { useModal } from "../../../context/ModalContext";
import { useUpdateMultipleSubscriptionPaymentMethod } from
  "../../../services/update-multiple-subscription-paymentMethod/update-multiple-subscription-paymentMethod.service";
import useWellnessPlansByUser from "../../../services/wellness-plans-by-user/wellness-plan-by-user.service";
import { userActivityLogger } from "../../../utils/mixpanel";

const AssignPaymentModal = () => {
  const { openModal, closeModal, newlyAddedPayment, setNewlyAddedPayment } = useModal();
  const { getWellnessPlansByUser, data, loading } = useWellnessPlansByUser();
  const { updateMultipleSubscription, data: updateSubscriptionData, loading: updateSubscriptionLoading, error } =
  useUpdateMultipleSubscriptionPaymentMethod();
  const [updateWellnessIds, setUpdateWellnessIds] = useState<string[]>([]);
  const dispatch = useDispatch();
  const [retryCheck, setRetryCheck] = useState<number>(3);
  const { setCardUpdateStatus } = useCardDetailStore();

  const getPetNames = (updatedWellnessIds: string[], wellnessData: IWellnessPlanByUser[]) => {
    const petNameList = wellnessData
      .filter((plan) => updatedWellnessIds.includes(plan.wellnessPlanDetails.wellnessId.toString()))
      .map((plan) => plan.pet.name);

    return petNameList;
  };


  useEffect(()=>{
    getWellnessPlansByUser();
  }, [updateSubscriptionData, error]);

  useEffect(()=>{
    if (retryCheck < 1) {
      setNewlyAddedPayment({ cardBrand: "", lastFourDigits: "", cardId: "" });
      openModal(MODALS.UPDATE_PAYMENT_ERROR_MODAL);
    }
  }, [retryCheck]);


  useEffect(()=>{
    if (updateSubscriptionData) {
      if (updateSubscriptionData.success && data) {
        if (updateSubscriptionData.failedWellnessIds.length) {
          dispatch(toastMessageActions.setGlobalAlert({
            severity: "warning",
            message: "Something went wrong, please try again.",
            customIcon: "info",
          }));
          userActivityLogger(MIXPANEL_EVENTS.USER_ASSIGN_PAYMENT_METHOD_FAILURE, {
            pet_name: getPetNames(updateWellnessIds, data),
            update_failed_pets: getPetNames(updateSubscriptionData.failedWellnessIds, data),
          });
          setRetryCheck(retryCheck-1);
        } else {
          dispatch(toastMessageActions.setGlobalAlert({
            severity: "info",
            message: updateSubscriptionData.messages[0],
            alertType: "updatePaymentAlert",
            customIcon: customIconsConfig.PAYMENT_UPDATE_ICON,
            customMessage: <span>
              <span className="!font-normal">{`${updateSubscriptionData.successfulWellnessIds.length === 1 ?
                "Subscription":"Subscriptions"} successfully updated with `}</span>
              <span className="capitalize">
                {newlyAddedPayment.cardBrand.toLowerCase().split("_").join(" ")}
                <span className="px-1 pb-[0.4rem]">····</span> {newlyAddedPayment.lastFourDigits}
              </span>
            </span>,
          }));
          userActivityLogger(MIXPANEL_EVENTS.USER_ASSIGN_PAYMENT_METHOD_SUCCESS, {
            pet_name: getPetNames(updateSubscriptionData.successfulWellnessIds, data),
          });

          setCardUpdateStatus(true);
          handleSuccessClose();
        }
      } else {
        dispatch(toastMessageActions.setGlobalAlert({
          severity: "warning",
          message: "Something went wrong, please try again.",
          customIcon: "info",
        }));
        if (data) {
          userActivityLogger(MIXPANEL_EVENTS.USER_ASSIGN_PAYMENT_METHOD_FAILURE, {
            pet_name: getPetNames(updateWellnessIds, data),
            error: updateSubscriptionData.messages[0],
          });
        }
        setRetryCheck(retryCheck-1);
      }
    } else if (error) {
      dispatch(toastMessageActions.setGlobalAlert({
        severity: "warning",
        message: "Something went wrong, please try again.",
        customIcon: "info",
      }));
      if (data) {
        userActivityLogger(MIXPANEL_EVENTS.USER_ASSIGN_PAYMENT_METHOD_FAILURE, {
          pet_name: getPetNames(updateWellnessIds, data),
          error: error.graphQLErrors[0].message,
        });
      }
      setRetryCheck(retryCheck-1);
    }
  }, [updateSubscriptionData, error]);

  const handleSuccessClose = () => {
    setNewlyAddedPayment({ cardBrand: "", lastFourDigits: "", cardId: "" });
    if (window.location.href.includes(PAGES_PATH.WELLNESS_PLAN_CONFIRM_AND_PAY)) {
      openModal(MODALS.UPDATE_PAYMENT_METHOD_MODAL);
    } else {
      closeModal();
    }
  };

  const handleSkipClose = () => {
    if (!updateSubscriptionLoading) {
      setNewlyAddedPayment({ cardBrand: "", lastFourDigits: "", cardId: "" });
      userActivityLogger(MIXPANEL_EVENTS.USER_SKIP_PAYMENT_METHOD_UPDATE, {});
      if (window.location.href.includes(PAGES_PATH.WELLNESS_PLAN_CONFIRM_AND_PAY)) {
        openModal(MODALS.UPDATE_PAYMENT_METHOD_MODAL);
      } else {
        closeModal();
      }
    }
  };

  const handleAssignClick = () => {
    if (updateWellnessIds.length) {
      if (data) {
        userActivityLogger(MIXPANEL_EVENTS.USER_ASSIGN_PAYMENT_METHOD_CONFIRM, {
          pet_name: getPetNames(updateWellnessIds, data),
        });
      }
      updateMultipleSubscription(updateWellnessIds, newlyAddedPayment.cardId);
    } else {
      handleSkipClose();
    }
  };


  return (
    <div className="flex flex-col gap-4 justify-between w-full h-full p-2">

      <div className="flex flex-col items-center gap-4">
        <div className="w-full flex justify-end">
          <CloseIcon
            onClick={handleSkipClose}
            className="text-light-black cursor-pointer"
          />
        </div>
        <div className="flex flex-col gap-2 items-center">
          <div className="text-2xl font-semibold">
          Payment Method Added
          </div>
          <div className="w-9/12 text-center">
          Would you like to assign this payment method
          to an existing plan?
          </div>
        </div>
      </div>
      <div className="bg-taupe-15 rounded-md flex justify-between px-3 py-[0.62rem] items-center">
        <CardProviderDetails
          cardBrand={newlyAddedPayment.cardBrand}
          lastFourDigits={newlyAddedPayment.lastFourDigits}
          textClass="!text-base"
        />
        <div className="bg-turquoise-1 rounded-full p-2">
          <SocCompleteIcon height={19} width={19} />
        </div>
      </div>
      <div className="w-full flex-1 overflow-y-scroll scroll-hide">
        {loading ?
          <CentralLoadingIndicator/>:
          data && (
            <AssignPaymentPetCardList
              wellnessPaymentData={data}
              setUpdateWellnessIds={setUpdateWellnessIds}
            />
          )
        }
      </div>
      <div className="w-full flex flex-col items-center gap-2">
        <StyledBookingFlowButton
          color="primary"
          disabled={loading || (updateWellnessIds.length ===0) || updateSubscriptionLoading}
          size="small"
          className="w-full"
          onClick={handleAssignClick}
        >
          {updateSubscriptionLoading?
            <CircularProgress color="inherit" size={20} />:
            "Assign to Selected Plan"}
        </StyledBookingFlowButton>
        <StyledBookingFlowButton
          color="primary"
          disabled={loading || updateSubscriptionLoading}
          size="small"
          className={`!bg-transparent w-full hover:!bg-gray-3
            ${loading || updateSubscriptionLoading ? "!text-gray-1" : "!text-black"}`}
          onClick={handleSkipClose}
        >
                  Skip for Now
        </StyledBookingFlowButton>

      </div>
    </div>
  );
};

export default AssignPaymentModal;
